import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Button, Chip, Alert,
  CircularProgress, Stack, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  LinearProgress, Tooltip, IconButton, Divider, Card, CardContent
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Warning as PartialIcon,
  Cancel as MissingIcon,
  FolderOpen as FolderIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';

const ALL_TYPES = [
  { value: 'birth_certificate',    label: 'Birth Certificate' },
  { value: 'transfer_certificate', label: 'Transfer Certificate' },
  { value: 'aadhaar',              label: 'Aadhaar Card' },
  { value: 'photo',                label: 'Passport Photo' },
  { value: 'report_card',          label: 'Previous Report Card' },
  { value: 'other',                label: 'Other' },
];

function ComplianceBadge({ student, size = 'medium' }) {
  const { total_required, total_matched, is_complete, missing_types } = student;

  if (total_required === 0) {
    return (
      <Chip
        label="No requirements set"
        size="small"
        sx={{ bgcolor: '#f5f5f5', color: '#9e9e9e', fontSize: '0.72rem' }}
      />
    );
  }
  if (is_complete) {
    return (
      <Chip
        icon={<CompleteIcon sx={{ fontSize: '0.9rem !important', color: '#2e7d32 !important' }} />}
        label="Complete"
        size="small"
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.72rem' }}
      />
    );
  }
  if (total_matched === 0) {
    return (
      <Tooltip title={`Missing: ${missing_types.map(t => ALL_TYPES.find(a => a.value === t)?.label || t).join(', ')}`}>
        <Chip
          icon={<MissingIcon sx={{ fontSize: '0.9rem !important', color: '#c62828 !important' }} />}
          label={`0 / ${total_required}`}
          size="small"
          sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.72rem' }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Missing: ${missing_types.map(t => ALL_TYPES.find(a => a.value === t)?.label || t).join(', ')}`}>
      <Chip
        icon={<PartialIcon sx={{ fontSize: '0.9rem !important', color: '#e65100 !important' }} />}
        label={`${total_matched} / ${total_required}`}
        size="small"
        sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.72rem' }}
      />
    </Tooltip>
  );
}

export default function RequiredDocuments() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [requiredTypes, setRequiredTypes] = useState([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configAlert, setConfigAlert] = useState(null);

  const [students, setStudents] = useState([]);
  const [complianceRequired, setComplianceRequired] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const { data } = await principalAPI.documents.getRequired();
      setRequiredTypes(data.required_types || []);
    } catch {
      setConfigAlert({ severity: 'error', message: 'Failed to load requirements.' });
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const loadCompliance = useCallback(async () => {
    setComplianceLoading(true);
    try {
      const { data } = await principalAPI.documents.compliance();
      setStudents(data.students || []);
      setComplianceRequired(data.required_types || []);
    } catch {
      // non-fatal
    } finally {
      setComplianceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadCompliance();
  }, [loadConfig, loadCompliance]);

  function toggleType(type) {
    setRequiredTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }

  async function handleSave() {
    setSaving(true);
    setConfigAlert(null);
    try {
      const { data } = await principalAPI.documents.setRequired(requiredTypes);
      setRequiredTypes(data.required_types);
      setConfigAlert({ severity: 'success', message: 'Requirements saved. Refreshing compliance…' });
      await loadCompliance();
      setConfigAlert({ severity: 'success', message: 'Requirements saved successfully.' });
    } catch {
      setConfigAlert({ severity: 'error', message: 'Failed to save requirements.' });
    } finally {
      setSaving(false);
    }
  }

  // Derived classes list for filter
  const classes = [...new Set(students.map(s => s.class_name).filter(Boolean))].sort();

  const filtered = students.filter(s => {
    const matchSearch = !search || s.student_name.toLowerCase().includes(search.toLowerCase()) || s.admission_number?.toLowerCase().includes(search.toLowerCase());
    const matchClass  = filterClass === 'all' || s.class_name === filterClass;
    const matchStatus = filterStatus === 'all'
      || (filterStatus === 'complete'  && s.is_complete)
      || (filterStatus === 'partial'   && !s.is_complete && s.total_matched > 0)
      || (filterStatus === 'missing'   && s.total_matched === 0 && s.total_required > 0)
      || (filterStatus === 'none'      && s.total_required === 0);
    return matchSearch && matchClass && matchStatus;
  });

  const stats = {
    total:    students.length,
    complete: students.filter(s => s.is_complete).length,
    partial:  students.filter(s => !s.is_complete && s.total_matched > 0 && s.total_required > 0).length,
    missing:  students.filter(s => s.total_matched === 0 && s.total_required > 0).length,
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" gap={2} mt={1} mb={0.5}>
          <Typography variant="h5" fontWeight={700} color="#1a3c5e">
            Document Requirements
          </Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/principal/dashboard')}>
            Back to Dashboard
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Set which document types are mandatory for every student, then track compliance school-wide.
        </Typography>

        {/* ── Configuration ── */}
        <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Required Document Types
          </Typography>

          {configLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {ALL_TYPES.map(({ value, label }) => (
                  <FormControlLabel
                    key={value}
                    control={
                      <Checkbox
                        checked={requiredTypes.includes(value)}
                        onChange={() => toggleType(value)}
                        size="small"
                        sx={{ '&.Mui-checked': { color: '#00796b' } }}
                      />
                    }
                    label={label}
                    sx={{
                      border: '1px solid',
                      borderColor: requiredTypes.includes(value) ? '#00796b' : 'grey.300',
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      bgcolor: requiredTypes.includes(value) ? '#e0f2f1' : 'transparent',
                      transition: 'all 0.15s',
                      m: 0,
                    }}
                  />
                ))}
              </Box>
              {configAlert && (
                <Alert severity={configAlert.severity} onClose={() => setConfigAlert(null)} sx={{ mb: 1.5 }}>
                  {configAlert.message}
                </Alert>
              )}
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ bgcolor: '#00796b', '&:hover': { bgcolor: '#004d40' } }}
              >
                {saving ? 'Saving…' : 'Save Requirements'}
              </Button>
            </>
          )}
        </Paper>

        {/* ── Compliance Overview ── */}
        <Paper sx={{ p: 3 }} elevation={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              Student Compliance
            </Typography>
            {complianceRequired.length === 0 && !complianceLoading && (
              <Chip label="No requirements configured" size="small" sx={{ bgcolor: '#f5f5f5', color: '#9e9e9e' }} />
            )}
          </Stack>

          {/* Summary stat chips */}
          {complianceRequired.length > 0 && (
            <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
              <Chip
                label={`${stats.complete} Complete`}
                size="small"
                onClick={() => setFilterStatus(filterStatus === 'complete' ? 'all' : 'complete')}
                sx={{ bgcolor: filterStatus === 'complete' ? '#2e7d32' : '#e8f5e9', color: filterStatus === 'complete' ? '#fff' : '#2e7d32', fontWeight: 600, cursor: 'pointer' }}
              />
              <Chip
                label={`${stats.partial} Partial`}
                size="small"
                onClick={() => setFilterStatus(filterStatus === 'partial' ? 'all' : 'partial')}
                sx={{ bgcolor: filterStatus === 'partial' ? '#e65100' : '#fff3e0', color: filterStatus === 'partial' ? '#fff' : '#e65100', fontWeight: 600, cursor: 'pointer' }}
              />
              <Chip
                label={`${stats.missing} Missing All`}
                size="small"
                onClick={() => setFilterStatus(filterStatus === 'missing' ? 'all' : 'missing')}
                sx={{ bgcolor: filterStatus === 'missing' ? '#c62828' : '#ffebee', color: filterStatus === 'missing' ? '#fff' : '#c62828', fontWeight: 600, cursor: 'pointer' }}
              />
            </Stack>
          )}

          {/* Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
            <TextField
              size="small"
              placeholder="Search student or admission no."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Class</InputLabel>
              <Select value={filterClass} label="Class" onChange={e => setFilterClass(e.target.value)}>
                <MenuItem value="all">All Classes</MenuItem>
                {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="missing">Missing All</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {complianceLoading ? (
            <Box py={4} display="flex" justifyContent="center"><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={FolderIcon}
              title="No students match the current filter."
              variant="no-results"
              dense
            />
          ) : (
            <ResponsiveDataView
              rows={filtered}
              keyExtractor={(s) => s.student_id}
              renderCard={(s) => (
                <Card
                  variant="outlined"
                  sx={{ mb: 1.5, bgcolor: s.is_complete ? '#f1fdf3' : 'transparent' }}
                  onClick={() => navigate(`/principal/students/${s.student_id}/documents`)}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{s.student_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.admission_number || '—'} · {s.class_name || 'Unassigned'}
                        </Typography>
                      </Box>
                      <ComplianceBadge student={s} />
                    </Box>
                    {s.missing_types.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} mt={1}>
                        {s.missing_types.map(t => (
                          <Chip
                            key={t}
                            label={ALL_TYPES.find(a => a.value === t)?.label || t}
                            size="small"
                            sx={{ bgcolor: '#ffebee', color: '#c62828', fontSize: '0.68rem', height: 20 }}
                          />
                        ))}
                      </Stack>
                    )}
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                      <Tooltip title="View Documents">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); navigate(`/principal/students/${s.student_id}/documents`); }}
                          sx={{ color: '#1565c0' }}
                        >
                          <FolderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              )}
              table={
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5', py: 1.25 } }}>
                        <TableCell>Student</TableCell>
                        <TableCell>Admission No.</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Missing Documents</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.map(s => (
                        <TableRow
                          key={s.student_id}
                          sx={{
                            bgcolor: s.is_complete ? '#f1fdf3' : 'transparent',
                            '&:hover': { bgcolor: s.is_complete ? '#e6f9e9' : '#fafafa' },
                            transition: 'background 0.15s',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>{s.student_name}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{s.admission_number || '—'}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{s.class_name || 'Unassigned'}</TableCell>
                          <TableCell>
                            <ComplianceBadge student={s} />
                          </TableCell>
                          <TableCell>
                            {s.missing_types.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            ) : (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                {s.missing_types.map(t => (
                                  <Chip
                                    key={t}
                                    label={ALL_TYPES.find(a => a.value === t)?.label || t}
                                    size="small"
                                    sx={{ bgcolor: '#ffebee', color: '#c62828', fontSize: '0.68rem', height: 20 }}
                                  />
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Documents">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/principal/students/${s.student_id}/documents`)}
                                sx={{ color: '#1565c0' }}
                              >
                                <FolderIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
            />
          )}
        </Paper>
    </Container>
  );
}
